import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
    Card, CardBody, CardHeader, Typography, Button,
    Chip, Dialog, DialogBody, DialogHeader, DialogFooter, Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSync, faEye,
    faSpinner, faCheckCircle,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

/**
 * PaymentAllocationAudit Component
 * Displays payment batch details with all allocations
 * Shows owner_trans records and cashbook entry links
 */
const PaymentAllocationAudit = () => {
    const { token } = useAuth();
    const { batchId } = useParams();
    const [batch, setBatch] = useState(null);
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAlloc, setSelectedAlloc] = useState(null);
    const [showAllocDetails, setShowAllocDetails] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (batchId) {
            fetchBatchDetails();
        }
    }, [batchId]);

    const fetchBatchDetails = async () => {
        setLoading(true);
        try {
            const result = await axios.get(
                `${rootUrl}/api/remittance/batch/${batchId}/allocations`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (result.data.success) {
                setBatch(result.data.batch);
                setAllocations(result.data.allocations);
            } else {
                setError('Error: ' + result.data.message);
            }
        } catch (error) {
            console.error('Error fetching batch details:', error);
            setError('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'POSTED':
                return 'bg-green-100 text-green-800';
            case 'VERIFIED':
                return 'bg-blue-100 text-blue-800';
            case 'ALLOCATED':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // eslint-disable-next-line no-unused-vars
    const getPaymentMethodColor = (method) => {
        switch (method) {
            case 'BANK_TRANSFER':
                return 'bg-blue-50 border-blue-200';
            case 'CHEQUE':
                return 'bg-purple-50 border-purple-200';
            case 'MPESA':
                return 'bg-green-50 border-green-200';
            case 'CASH':
                return 'bg-yellow-50 border-yellow-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faSpinner} className="text-4xl text-[#FF5733] animate-spin mb-4" />
                    <Typography variant="h5">Loading batch details...</Typography>
                </div>
            </div>
        );
    }

    if (!batch) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
                <div className="max-w-7xl mx-auto">
                    <Button
                        variant="text"
                        className="mb-6 text-[#FF5733]"
                        onClick={() => window.history.back()}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Back
                    </Button>
                    <Card>
                        <CardBody className="text-center p-10">
                            <Typography variant="h6" className="text-gray-600">
                                Batch not found
                            </Typography>
                        </CardBody>
                    </Card>
                </div>
            </div>
        );
    }

    const totalAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.allocated_amount || 0), 0);
    const postedCount = allocations.filter(a => a.allocation_status === 'POSTED').length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="max-w-7xl mx-auto">
                    <Button
                        variant="text"
                        className="mb-4 text-[#FF5733]"
                        onClick={() => window.history.back()}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Back to Remittances
                    </Button>

                    <div className="mb-4">
                        {error && (
                            <Alert
                                color="red"
                                icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                                className="mb-4"
                                onClose={() => setError(null)}
                            >
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert
                                color="green"
                                icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                className="mb-4"
                                onClose={() => setSuccess(null)}
                            >
                                {success}
                            </Alert>
                        )}
                    </div>

                    <div className="flex justify-between items-start">
                        <div>
                            <Typography variant="h4" className="font-bold text-[#2a2e30]">
                                Payment Batch Allocation Audit
                            </Typography>
                            <Typography variant="small" className="text-gray-600 mt-1">
                                Complete allocation details and transaction links
                            </Typography>
                        </div>
                        <Button
                            variant="outlined"
                            size="sm"
                            onClick={fetchBatchDetails}
                            className="border-[#FF5733] text-[#FF5733]"
                        >
                            <FontAwesomeIcon icon={faSync} className="mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Batch Details Card */}
                <Card className="mb-8 shadow-lg">
                    <CardHeader className="bg-gradient-to-r from-[#FF5733] to-orange-600 text-white p-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <Typography variant="h5" className="font-bold mb-2">
                                    {batch.batch_reference}
                                </Typography>
                                <Typography variant="small" className="opacity-90">
                                    {formatDate(batch.payment_date)} • {batch.payment_method}
                                </Typography>
                            </div>
                            <Chip
                                value={`${postedCount}/${allocations.length} Posted`}
                                className="bg-white text-[#FF5733] font-bold"
                            />
                        </div>
                    </CardHeader>

                    <CardBody className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold">
                                    Total Amount
                                </Typography>
                                <Typography variant="h5" className="text-[#FF5733] font-bold">
                                    {formatCurrency(batch.total_amount)}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold">
                                    Allocations
                                </Typography>
                                <Typography variant="h5" className="text-[#2a2e30] font-bold">
                                    {allocations.length}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold">
                                    Posted
                                </Typography>
                                <Typography variant="h5" className="text-green-600 font-bold">
                                    {postedCount}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold">
                                    Paid By
                                </Typography>
                                <Typography variant="small" className="font-semibold text-[#2a2e30]">
                                    {batch.paid_by}
                                </Typography>
                                <Typography variant="small" className="text-gray-600">
                                    {formatDate(batch.input_date)}
                                </Typography>
                            </div>
                        </div>

                        {batch.description && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <Typography variant="small" className="text-blue-800">
                                    <strong>Description:</strong> {batch.description}
                                </Typography>
                            </div>
                        )}

                        {batch.notes && (
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <Typography variant="small" className="text-yellow-800">
                                    <strong>Notes:</strong> {batch.notes}
                                </Typography>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Allocations Table */}
                <Card className="shadow-lg">
                    <CardHeader className="bg-gray-100 border-b border-gray-200 p-4">
                        <Typography variant="h6" className="font-bold text-[#2a2e30]">
                            Allocation Details ({allocations.length})
                        </Typography>
                    </CardHeader>

                    <CardBody className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left font-semibold text-gray-700">Owner</th>
                                        <th className="px-6 py-3 text-left font-semibold text-gray-700">Car</th>
                                        <th className="px-6 py-3 text-left font-semibold text-gray-700">Week</th>
                                        <th className="px-6 py-3 text-right font-semibold text-gray-700">Amount</th>
                                        <th className="px-6 py-3 text-center font-semibold text-gray-700">Status</th>
                                        <th className="px-6 py-3 text-center font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allocations.map((alloc, idx) => (
                                        <tr
                                            key={alloc.id}
                                            className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                } hover:bg-blue-50 transition-colors`}
                                        >
                                            <td className="px-6 py-4">
                                                <div>
                                                    <Typography variant="small" className="font-semibold text-gray-900">
                                                        {alloc.owner_code}
                                                    </Typography>
                                                    <Typography variant="small" className="text-gray-600">
                                                        {alloc.owner_name}
                                                    </Typography>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <Typography variant="small" className="font-semibold text-gray-900">
                                                        {alloc.plate_no}
                                                    </Typography>
                                                    <Typography variant="small" className="text-gray-600">
                                                        {alloc.car_make} {alloc.car_model}
                                                    </Typography>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Typography variant="small" className="font-semibold">
                                                    {alloc.week_key}
                                                </Typography>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Typography variant="small" className="font-bold text-[#FF5733]">
                                                    {formatCurrency(alloc.allocated_amount)}
                                                </Typography>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Chip
                                                    value={alloc.allocation_status}
                                                    className={`font-semibold text-xs ${getStatusColor(alloc.allocation_status)}`}
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Button
                                                    variant="text"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedAlloc(alloc);
                                                        setShowAllocDetails(true);
                                                    }}
                                                    className="text-[#FF5733] hover:text-red-700"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {/* Total Row */}
                                    <tr className="bg-orange-50 font-bold border-t-2 border-gray-300">
                                        <td colSpan="3" className="px-6 py-4 text-right">
                                            TOTAL:
                                        </td>
                                        <td className="px-6 py-4 text-right text-[#FF5733]">
                                            {formatCurrency(totalAllocated)}
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Allocation Details Modal */}
            <Dialog open={showAllocDetails} handler={() => setShowAllocDetails(false)} size="lg">
                <DialogHeader>Allocation Details - {selectedAlloc?.owner_code}</DialogHeader>
                <DialogBody divider className="space-y-4">
                    {selectedAlloc && (
                        <>
                            {/* Basic Info */}
                            <Card className="bg-blue-50 border border-blue-200">
                                <CardBody className="p-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Typography variant="small" className="text-gray-600 font-semibold">
                                                Owner
                                            </Typography>
                                            <Typography variant="small" className="font-bold">
                                                {selectedAlloc.owner_code} - {selectedAlloc.owner_name}
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" className="text-gray-600 font-semibold">
                                                Car
                                            </Typography>
                                            <Typography variant="small" className="font-bold">
                                                {selectedAlloc.plate_no} ({selectedAlloc.car_make} {selectedAlloc.car_model})
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" className="text-gray-600 font-semibold">
                                                Week
                                            </Typography>
                                            <Typography variant="small" className="font-bold">
                                                {selectedAlloc.week_key}
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" className="text-gray-600 font-semibold">
                                                Allocated Amount
                                            </Typography>
                                            <Typography variant="small" className="font-bold text-[#FF5733]">
                                                {formatCurrency(selectedAlloc.allocated_amount)}
                                            </Typography>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>

                            {/* Transaction Links */}
                            <Card className="bg-green-50 border border-green-200">
                                <CardBody className="p-4">
                                    <Typography variant="small" className="font-semibold text-gray-700 mb-3">
                                        Transaction References
                                    </Typography>
                                    <div className="space-y-2">
                                        {selectedAlloc.owner_trans_id_approval && (
                                            <div className="flex items-center justify-between p-2 bg-white rounded border border-green-100">
                                                <div>
                                                    <Typography variant="small" className="text-gray-600">
                                                        Approval Transaction (REM)
                                                    </Typography>
                                                    <Typography variant="small" className="font-mono font-bold">
                                                        {selectedAlloc.owner_trans_id_approval}
                                                    </Typography>
                                                </div>
                                                <Chip value="Linked" className="bg-green-100 text-green-800" />
                                            </div>
                                        )}
                                        {selectedAlloc.owner_trans_id_payment && (
                                            <div className="flex items-center justify-between p-2 bg-white rounded border border-green-100">
                                                <div>
                                                    <Typography variant="small" className="text-gray-600">
                                                        Payment Transaction (PAY)
                                                    </Typography>
                                                    <Typography variant="small" className="font-mono font-bold">
                                                        {selectedAlloc.owner_trans_id_payment}
                                                    </Typography>
                                                </div>
                                                <Chip value="Linked" className="bg-green-100 text-green-800" />
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>

                            {/* Status Info */}
                            <Card>
                                <CardBody className="p-4">
                                    <Typography variant="small" className="font-semibold text-gray-700 mb-3">
                                        Status Information
                                    </Typography>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Status:</span>
                                            <Chip
                                                value={selectedAlloc.allocation_status}
                                                className={`font-semibold text-xs ${getStatusColor(selectedAlloc.allocation_status)}`}
                                            />
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Created:</span>
                                            <span className="font-semibold">{formatDate(selectedAlloc.input_date)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Created By:</span>
                                            <span className="font-semibold">{selectedAlloc.input_by}</span>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={() => setShowAllocDetails(false)}
                    >
                        Close
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default PaymentAllocationAudit;
